/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  ApplicationMethod,
  Dose,
  Medication,
  MedicationDoseUnit,
  MedicationSource,
  MedicationValue,
} from '../../../../backendModels/medication.model';
import { Autocomplete, TextField } from '@mui/material';
import {
  convertToApplicationMethodLabels,
  convertToMedicationListLabels,
  dosageUnitLabels,
} from '../../../../models/medication';
import CreatableAutocomplete, { OptionValueType } from '../../../customs/creatableAutocomplete/CreatableAutocomplete';
import TextFieldSelectGroup from '../../../customs/textFieldSelectGroup/TextFieldSelectGroup';
import _ from 'lodash';
import { CheckCircleOutline } from '@mui/icons-material';
import style from './AddMedicationDialog.scss?inline';
import { useCSS } from '../../../../provider/CSSProvider';
import dayjs, { Dayjs } from 'dayjs';
import { TimePicker } from '../../../customs/timePicker/TimePicker';
import { hhmmToCorrectedDateJS } from '../../../../utils/util';
import { createMedication } from './AddMedicationDialog.functions';

export interface AddMedicationDialogResponse {
  timestamp: Dayjs;
  medication: MedicationValue;
}

interface AddMedicationDialogProps {
  /**
   * The list of all available medications coming from the configuration.
   */
  readonly medicationList: readonly Medication[];
  /**
   * The list of all application methods coming from the configuration.
   */
  readonly applicationMethods: readonly ApplicationMethod[];
  readonly open: boolean;
  readonly onOpenChanged: (open: boolean) => void;
  /**
   * Callback that is invoked when a new medication is added.
   * @param response The medication with timestamp that was added.
   */
  readonly onConfirm: (response: AddMedicationDialogResponse) => void;
}

export default function AddMedicationDialog(props: AddMedicationDialogProps) {
  useCSS(style);

  const { medicationList, applicationMethods, open, onOpenChanged: setOpen, onConfirm } = props;

  const [medicationName, setMedicationName] = useState<string | undefined>(undefined);
  const [dosageValue, setDosageValue] = useState<number | undefined>(undefined);
  const [dosageUnit, setDosageUnit] = useState<MedicationDoseUnit>(MedicationDoseUnit.MILLIGRAM);
  const [applicationMethod, setApplicationMethod] = useState<string | undefined>(undefined);
  const [medicationSource, setMedicationSource] = useState<MedicationSource>(MedicationSource.CONFIGURATION);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const [timestamp, setTimestamp] = useState(dayjs().format('HH:mm'));

  function isAddable(): boolean {
    return (
      medicationName !== undefined &&
      dosageValue !== undefined &&
      dosageUnit !== undefined &&
      applicationMethod !== undefined &&
      timestamp !== ''
    );
  }

  function handleAdd() {
    if (!isAddable()) {
      return;
    }
    const dose: Dose = {
      value: dosageValue!,
      unit: dosageUnit,
    };
    const medication = createMedication(medicationSource, medicationName, dose, applicationMethod, medicationList);
    onConfirm({ timestamp: hhmmToCorrectedDateJS(timestamp), medication: medication });
    setIsConfirmed(true);

    setTimeout(handleClose, 1000);
  }

  function handleClose() {
    setOpen(false);
    setTimeout(() => {
      setMedicationName(undefined);
      setDosageValue(undefined);
      setDosageUnit(MedicationDoseUnit.MILLIGRAM);
      setApplicationMethod(undefined);
      setMedicationSource(MedicationSource.CONFIGURATION);
      setTimestamp('');
      setIsConfirmed(false);
    }, 200);
  }

  function medicationKeyChanged(optionValue: OptionValueType<string>) {
    if (optionValue.customInputValue === undefined) {
      setMedicationSource(MedicationSource.CONFIGURATION);
    } else {
      setMedicationSource(MedicationSource.CUSTOM);
    }
    setMedicationName(optionValue.value);
    if (optionValue.customInputValue === undefined) {
      const medication = _.find(medicationList, { key: optionValue.value });
      if (medication?.defaultUnit != null) {
        setDosageUnit(medication.defaultUnit);
      }
      if (medication?.defaultApplicationMethod != null) {
        setApplicationMethod(medication.defaultApplicationMethod);
      }
    }
  }

  function dosageValueChanged(dosage: string) {
    if (dosage !== '') {
      setDosageValue(Number(dosage));
    } else {
      setDosageValue(undefined);
    }
  }

  const applicationMethodLabels = useMemo(
    () => convertToApplicationMethodLabels(applicationMethods),
    [applicationMethods]
  );

  useEffect(() => {
    if (open) {
      setTimestamp(dayjs().format('HH:mm'));
    }
  }, [open]);

  const medicationOptions = useMemo(() => convertToMedicationListLabels(medicationList), [medicationList]);

  return (
    <Dialog open={open} onClose={handleClose} className='add-medication-dialog' closeAfterTransition={false}>
      <DialogTitle>Medikation hinzufügen</DialogTitle>
      <DialogContent>
        {isConfirmed ? (
          <div className='add-medication-dialog-confirmed'>
            <CheckCircleOutline className='check-circle' />
          </div>
        ) : (
          <div className='add-margin-top'>
            <TimePicker label='Zeitpunkt' required={true} value={timestamp} onChange={setTimestamp} />
            <CreatableAutocomplete
              options={medicationOptions}
              newValueCreatorFunction={(value: string) => value}
              onChange={medicationKeyChanged}
              label='Medikament'
            />
            <TextFieldSelectGroup
              fullWidth
              type='number'
              label={`Dosis [${dosageUnitLabels[dosageUnit]}]`}
              options={Object.entries(dosageUnitLabels) as [MedicationDoseUnit, string][]}
              selectedOption={dosageUnit}
              onSelectedOptionChange={setDosageUnit}
              onDebounceChange={dosageValueChanged}
              debounceTime={10}
              value={dosageValue?.toString() ?? ''}
            />
            <Autocomplete
              options={applicationMethodLabels}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => setApplicationMethod(value?.value)}
              renderInput={(params) => <TextField {...params} label='Applikationsform' />}
              value={_.find(applicationMethodLabels, { value: applicationMethod }) ?? null}
            />
          </div>
        )}
      </DialogContent>
      {!isConfirmed && (
        <DialogActions>
          <Button className='cancel-button' onClick={handleClose} variant='outlined'>
            Abbrechen
          </Button>
          <Button onClick={handleAdd} autoFocus disabled={!isAddable()} variant='contained'>
            Hinzufügen
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
